/**
 * Timeline Ruler Component
 *
 * Displays time ruler with tick marks and labels showing seconds/frames.
 */

import { useMemo } from 'react'
import { generateRulerTicks } from './timelineUtils'

interface TimelineRulerProps {
  /** Total duration of timeline in seconds */
  totalDuration: number

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Viewport width in pixels */
  viewportWidth: number

  /** Height of ruler in pixels */
  height?: number
}

export function TimelineRuler({
  totalDuration,
  zoomLevel,
  viewportWidth,
  height = 30,
}: TimelineRulerProps) {
  const ticks = useMemo(
    () => generateRulerTicks(totalDuration, zoomLevel, viewportWidth),
    [totalDuration, zoomLevel, viewportWidth]
  )

  const timelineWidth = Math.max(totalDuration * zoomLevel, viewportWidth)

  return (
    <div
      className="relative bg-canvas border-b border-border"
      style={{ width: `${timelineWidth}px`, height: `${height}px` }}
    >
      {/* Tick marks */}
      {ticks.map((tick, index) => (
        <div
          key={index}
          className="absolute top-0"
          style={{ left: `${tick.position}px` }}
        >
          {/* Tick line */}
          <div
            className={`${tick.isMajor ? 'bg-white/40 h-4' : 'bg-border h-2'} w-px`}
          />

          {/* Tick label */}
          {tick.label && (
            <div className="absolute top-4 left-0 -translate-x-1/2 text-text-muted text-xs whitespace-nowrap">
              {tick.label}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
